import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquare, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { createChatConversation, deleteChatConversation, getChatConversation, getChatStatus, getModelCatalog, listChatConversations, sendChatMessage } from "@/lib/api";
import { MAX_TEXT, UNAVAILABLE_TEXT, canSend, errorText, messageStatusText, newRequestId } from "@/lib/chat";
import { normalizeModels } from "@/lib/modelHub";

export default function ChatAI() {
  const [state, setState] = useState({ status: "loading" });
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const pending = useRef(null);          // { text, id }: the same text keeps the same request id until it succeeds
  const lock = useRef(false);

  useEffect(() => {
    let alive = true;
    getChatStatus().then(async (s) => {
      if (!alive) return;
      if (!s?.enabled) { setState({ status: "disabled" }); return; }
      const [catalog, list] = await Promise.all([getModelCatalog("chat").catch(() => ({ models: [] })), listChatConversations().catch(() => ({ conversations: [] }))]);
      if (!alive) return;
      const chat = normalizeModels(catalog).filter((m) => m.category === "chat");
      setModels(chat); setModel(chat[0]?.model || ""); setConversations(list.conversations || []); setState({ status: "ready" });
    }).catch(() => { if (alive) setState({ status: "error" }); });
    return () => { alive = false; };
  }, []);

  const open = async (id) => {
    setActive(id); setNotice("");
    try { const data = await getChatConversation(id); setMessages(data.messages || []); } catch (error) { setMessages([]); setNotice(errorText(error)); }
  };
  const start = async () => {
    if (lock.current || !model) return;
    lock.current = true; setBusy(true); setNotice("");
    try { const c = await createChatConversation(model); setConversations((l) => [c, ...l]); await open(c.id); } catch (error) { setNotice(errorText(error)); } finally { lock.current = false; setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Hapus percakapan ini?")) return;
    try { await deleteChatConversation(id); setConversations((l) => l.filter((c) => c.id !== id)); if (active === id) { setActive(null); setMessages([]); } } catch (error) { setNotice(errorText(error)); }
  };
  const send = async () => {
    if (lock.current || !active || !canSend(text, busy)) return;
    lock.current = true; setBusy(true); setNotice("");
    if (!pending.current || pending.current.text !== text) pending.current = { text, id: newRequestId() };
    try {
      await sendChatMessage(active, text, pending.current.id);
      pending.current = null; setText(""); await open(active);
    } catch (error) { await open(active); setNotice(errorText(error)); } finally { lock.current = false; setBusy(false); }   // refresh first: open() clears the notice
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6" data-testid="chat-page">
      <div className="flex items-center gap-2"><MessageSquare className="h-6 w-6 text-slate-700" /><h1 className="text-xl font-semibold text-slate-900">Chat AI</h1></div>
      {state.status === "loading" && <p className="mt-4 flex items-center gap-2 text-sm text-slate-500" data-testid="chat-loading"><Loader2 className="h-4 w-4 animate-spin" /> Memuat…</p>}
      {state.status === "error" && <p className="mt-4 text-sm text-amber-800" role="alert" data-testid="chat-error">Status Chat AI tidak dapat dimuat. Fitur dianggap belum tersedia.</p>}
      {state.status === "disabled" && <p className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" data-testid="chat-disabled">{UNAVAILABLE_TEXT}</p>}
      {state.status === "ready" && (
        <div className="mt-4 grid gap-4 md:grid-cols-[14rem_1fr]">
          <aside>
            {models.length === 0 ? <p className="text-sm text-slate-500" data-testid="chat-no-models">Belum ada model chat yang siap dipakai.</p> : (
              <><select className="w-full rounded border border-slate-200 px-2 py-1 text-sm" value={model} onChange={(e) => setModel(e.target.value)} data-testid="chat-model">{models.map((m) => <option key={m.model} value={m.model}>{m.title}</option>)}</select>
                <Button className="mt-2 w-full" disabled={busy || !model} onClick={start} data-testid="chat-new">Percakapan baru</Button></>)}
            <ul className="mt-3 space-y-1">{conversations.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-1 text-sm" data-testid={`chat-conv-${c.id}`}>
                <button type="button" className={`truncate text-left ${active === c.id ? "font-semibold" : ""}`} onClick={() => open(c.id)}>{c.title}</button>
                <button type="button" aria-label="Hapus percakapan" onClick={() => remove(c.id)} data-testid={`chat-del-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-slate-400" /></button></li>))}</ul>
          </aside>
          <section>
            {!active ? <p className="text-sm text-slate-500" data-testid="chat-pick">Pilih atau mulai percakapan.</p> : (
              <><ul className="space-y-2" data-testid="chat-messages">{messages.map((m) => (
                <li key={m.id} className={`rounded p-2 text-sm ${m.role === "user" ? "bg-blue-50" : "bg-slate-50"}`} data-testid={`chat-msg-${m.role}`}>
                  <span className="block whitespace-pre-wrap">{m.text}</span>{messageStatusText(m.status) && <span className="block text-xs text-slate-500" data-testid="chat-msg-status">{messageStatusText(m.status)}</span>}</li>))}</ul>
                <Textarea className="mt-3" value={text} maxLength={MAX_TEXT} onChange={(e) => setText(e.target.value)} placeholder="Tulis pesan…" data-testid="chat-input" />
                <Button className="mt-2" disabled={!canSend(text, busy)} onClick={send} data-testid="chat-send">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Kirim</Button></>)}
            {notice && <p className="mt-2 text-sm text-slate-700" role="status" data-testid="chat-notice">{notice}</p>}
          </section>
        </div>
      )}
    </div>
  );
}
